import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { apiUrl } from "../auth/config";
import { useAuth } from "../auth/AuthContext";
import "../styles/app.css";
import "../styles/proofs.css";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "proof", label: "Proof" },
  { value: "approved", label: "Approved" },
  { value: "sidelined", label: "Sidelined" },
  { value: "declined", label: "Declined" },
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
];

export default function Proofs() {
  const { user, loading, isAuthenticated, isAdmin, logout, getIdToken } =
    useAuth();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState("proof");
  const [error, setError] = useState("");
  const [info, setInfo] = useState(location.state?.flash || "");
  const [fetching, setFetching] = useState(true);

  const load = useCallback(async () => {
    setFetching(true);
    setError("");
    try {
      const token = await getIdToken();
      const qs = status ? `?status=${encodeURIComponent(status)}` : "";
      const res = await fetch(`${apiUrl}/proofs${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        setError("Admin role required to manage blog proofs.");
        setPosts([]);
        return;
      }
      if (!res.ok) throw new Error(`Could not load proofs (${res.status})`);
      const data = await res.json();
      setPosts(Array.isArray(data.posts) ? data.posts : []);
    } catch (err) {
      setError(err.message || "Failed to load proofs");
      setPosts([]);
    } finally {
      setFetching(false);
    }
  }, [getIdToken, status]);

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;
    load();
  }, [isAuthenticated, isAdmin, load]);

  if (loading) {
    return (
      <div className="app-shell">
        <main className="app-main">
          <p className="leads-support">Loading…</p>
        </main>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/signin" replace />;

  if (!isAdmin) {
    return (
      <div className="app-shell">
        <header className="app-header">
          <Link className="app-header__brand" to="/">
            FTOJ
          </Link>
          <div className="app-header__meta">
            <Link to="/leads">Inbox</Link>
            <button className="btn btn--ghost" type="button" onClick={() => logout()}>
              Sign out
            </button>
          </div>
        </header>
        <main className="app-main">
          <section className="leads-panel">
            <h2>Admin only</h2>
            <p>
              Blog proofs require an <strong>admin</strong> account. Ask an
              operator to promote your user if you need access.
            </p>
            <Link to="/leads">← Back to inbox</Link>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <Link className="app-header__brand" to="/">
          FTOJ
        </Link>
        <div className="app-header__meta">
          <Link to="/leads">Inbox</Link>
          <Link to="/proofs">Proofs</Link>
          <Link to="/security">Security</Link>
          <span>{user?.signInDetails?.loginId || user?.username}</span>
          <button className="btn btn--ghost" type="button" onClick={() => logout()}>
            Sign out
          </button>
        </div>
      </header>

      <main className="app-main">
        <div className="leads-heading">
          <div>
            <h1 className="leads-title">Blog proofs</h1>
            <p className="leads-support">
              SEO drafts land here for review. Approve → daily job publishes ≤1
              into the public export (runtime on JBL). Delete of a published
              post refreshes that export immediately.
            </p>
          </div>
          <button className="btn btn--ghost" type="button" onClick={load} disabled={fetching}>
            Refresh
          </button>
        </div>

        <div className="proof-filters">
          <label>
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_FILTERS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error ? <p className="auth-error">{error}</p> : null}
        {info ? <p className="auth-info">{info}</p> : null}
        {fetching ? <p className="leads-support">Loading…</p> : null}

        {!fetching && posts.length === 0 ? (
          <section className="leads-panel">
            <h2>No posts</h2>
            <p>
              Seed a sample with{" "}
              <code>./scripts/seed-blog-proof.sh staging</code> or POST{" "}
              <code>/proofs</code>.
            </p>
          </section>
        ) : null}

        <ul className="proof-list">
          {posts.map((p) => (
            <li key={p.postId}>
              <Link className="proof-row" to={`/proofs/${p.postId}`}>
                <div>
                  <p className="proof-row__title">{p.title}</p>
                  <p className="proof-row__meta">
                    {p.category} · {p.slug}
                  </p>
                </div>
                <span className={`proof-status proof-status--${p.status}`}>
                  {p.status}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
